import { Component } from 'react';
import { Box, Button, Typography } from '@mui/material';

/**
 * Section-level error boundary. Wraps a single dashboard section (a KPI group,
 * a table, or a chart) so that if that one section throws during render, only
 * that section is replaced with a small "This section failed to load — Retry"
 * card — the rest of the page keeps working. This sits INSIDE the route-level
 * boundary (RouteErrorBoundary), which remains the catch-all.
 *
 * @augments {Component<{ children: React.ReactNode, label?: string }, { error: Error | null }>}
 */
export default class SectionErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, componentStack: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ componentStack: info?.componentStack ?? null });
    console.error(
      `[section:${this.props.label || 'unnamed'}] render error:`,
      error,
      info?.componentStack
    );
  }

  handleRetry = () => this.setState({ error: null, componentStack: null });

  render() {
    if (this.state.error) {
      return (
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            my: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 1,
            backgroundColor: 'background.paper',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            This section failed to load.
          </Typography>
          {/* CRA equivalent of Vite's DEV flag */}
          {process.env.NODE_ENV !== 'production' ? (
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1,
                width: '100%',
                maxHeight: 220,
                overflow: 'auto',
                backgroundColor: '#fff1f2',
                border: '1px solid #fecdd3',
                borderRadius: 1,
                color: '#9f1239',
                fontSize: '11px',
                lineHeight: 1.4,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {`[section:${this.props.label || 'unnamed'}] ${this.state.error?.message || String(this.state.error)}`}
              {this.state.error?.stack ? `\n\n${this.state.error.stack}` : ''}
              {this.state.componentStack ? `\n\nComponent stack:${this.state.componentStack}` : ''}
            </Box>
          ) : null}
          <Button size="small" variant="outlined" onClick={this.handleRetry}>
            Retry
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}
